import { ZodType, z } from 'zod'

import {
  CHAT_BANNER_ACTIONS,
  CHAT_BANNER_BUTTON_SIZES,
  CHAT_BANNER_CLICKED_PARTS,
  CHAT_BANNER_EVENTS,
  CHAT_BANNER_POSITIONS,
  CHAT_BANNER_RATIOS,
  CHAT_BANNER_RATIOS_BY_STYLE,
  CHAT_BANNER_STYLES,
} from '@/constants/chat-banner'
import { ButtonSize, ButtonStyle, ButtonPosition, ButtonEffect, ButtonMode } from '@/constants/chatbot'
import { LANGUAGE_VALUES } from '@/constants/realtime-voice-support'
import { URL_REGEX } from '@/lib/constants'
import { isHexColor } from '@/lib/utils'

// Chatbot logo
export const CHATBOT_LOGO_SUPPORTED_FORMATS = ['.png', '.jpg', '.jpeg', '.webp']
export const CHATBOT_LOGO_DATA_SIZE_LIMIT = 2 // MB
export const CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

// Chatbot welcome message image
export const WELCOME_MESSAGE_IMAGE_SUPPORTED_FORMATS = ['.png', '.jpg', '.jpeg', '.webp', '.gif']
export const WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT = 2 // MB
export const WELCOME_MESSAGE_IMAGE_SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]

// Voice-to-text uploads: the containers MediaRecorder produces, keyed by bare MIME type
export const AUDIO_UPLOAD_EXTENSIONS: ReadonlyMap<string, string> = new Map([
  ['audio/webm', 'webm'],
  ['audio/mp4', 'mp4'],
])

export const ChatbotUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(30),
    welcomeMessage: z.string().trim().min(1).max(500),
    welcomeMessageImage: z.any() as ZodType<File>,
    welcomeMessageImageLink: z
      .string()
      .trim()
      .max(1000)
      .refine(
        (value) => {
          if (!value) return true
          return URL_REGEX.test(value)
        },
        {
          params: {
            i18n: {
              key: 'custom.url.invalid',
            },
          },
        },
      ),
    shouldDeleteWelcomeMessageImage: z.boolean().default(false),
    themeColor: z.string(),
    showTextSizeButton: z.boolean().default(true),
    bubbleLabel: z.string().trim().min(1).max(15),
    logo: z.any() as ZodType<File>,
    header: z.string().trim().min(1).max(30),
    connectedResponse: z.string().trim().min(1).max(70),
    questionInputPrompt: z.string().trim().min(1).max(70),
    enabledEscalation: z.boolean(),
    isCollectContactInfo: z.boolean(),
    collectionContactPrompt: z.string().trim().min(1).max(500),
    buttonDesignSettings: z.object({
      mode: z.nativeEnum(ButtonMode).optional(),
      size: z.nativeEnum(ButtonSize).optional(),
      style: z.nativeEnum(ButtonStyle).optional(),
      position: z.nativeEnum(ButtonPosition).optional(),
      effect: z.nativeEnum(ButtonEffect).optional(),
    }),
    escalationMessage: z.string().trim().min(1).max(500),
  })
  .refine(
    ({ logo }) => {
      // only validate if logo is present
      if (!logo?.size) return true
      // check if file format is supported
      const imageFormat = `.${logo?.name.split('.').pop()}`.toLowerCase()

      return (
        imageFormat &&
        CHATBOT_LOGO_SUPPORTED_FORMATS.includes(imageFormat) &&
        CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES.includes(logo?.type)
      )
    },
    {
      params: {
        i18n: {
          key: 'custom.file.not_supported',
          values: { formats: CHATBOT_LOGO_SUPPORTED_FORMATS.join(', ') },
        },
      },
      path: ['logo'],
    },
  )
  .refine(
    ({ logo }) => {
      // check if file size is less than CHATBOT_LOGO_DATA_SIZE_LIMIT
      const imageSize = logo?.size || 0
      const imageSizeInMB = imageSize / (1024 * 1024)

      return imageSizeInMB <= CHATBOT_LOGO_DATA_SIZE_LIMIT
    },
    {
      params: {
        i18n: {
          key: 'custom.file.too_large',
          values: { maxSize: `${CHATBOT_LOGO_DATA_SIZE_LIMIT}MB` },
        },
      },
      path: ['logo'],
    },
  )
  .refine(
    ({ themeColor }) => {
      return isHexColor(themeColor)
    },
    {
      params: {
        i18n: { key: 'custom.others.invalid' },
      },
      path: ['themeColor'],
    },
  )
  .refine(
    ({ welcomeMessageImage }) => {
      // only validate if welcomeMessageImage is present
      if (!welcomeMessageImage?.size) return true
      // check if file format is supported
      const imageFormat = `.${welcomeMessageImage?.name.split('.').pop()}`.toLowerCase()

      return (
        imageFormat &&
        WELCOME_MESSAGE_IMAGE_SUPPORTED_FORMATS.includes(imageFormat) &&
        WELCOME_MESSAGE_IMAGE_SUPPORTED_IMAGE_TYPES.includes(welcomeMessageImage?.type)
      )
    },
    {
      params: {
        i18n: {
          key: 'custom.file.not_supported',
          values: { formats: WELCOME_MESSAGE_IMAGE_SUPPORTED_FORMATS.join(', ') },
        },
      },
      path: ['welcomeMessageImage'],
    },
  )
  .refine(
    ({ welcomeMessageImage }) => {
      // check if file size is less than WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT
      const imageSize = welcomeMessageImage?.size || 0
      const imageSizeInMB = imageSize / (1024 * 1024)

      return imageSizeInMB <= WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT
    },
    {
      params: {
        i18n: {
          key: 'custom.file.too_large',
          values: { maxSize: `${WELCOME_MESSAGE_IMAGE_DATA_SIZE_LIMIT}MB` },
        },
      },
      path: ['welcomeMessageImage'],
    },
  )

export const ChatbotUpdateConversationStyleSchema = z.object({
  conversationStyle: z
    .object({
      value: z.string().trim(),
      custom: z.string().trim(),
    })
    .refine(({ value, custom }) => (value === 'custom' ? custom.trim().length > 0 : true), {
      params: {
        i18n: {
          key: 'errors.too_small.string.inclusive',
          values: { minimum: 1 },
        },
      },
      path: ['custom'],
    })
    .refine(({ value, custom }) => (value === 'custom' ? custom.trim().length <= 1000 : true), {
      params: {
        i18n: {
          key: 'errors.too_big.string.inclusive',
          values: { maximum: 1000 },
        },
      },
      path: ['custom'],
    }),
})
export type ChatbotUpdateConversationStyleSchemaType = z.infer<typeof ChatbotUpdateConversationStyleSchema>

const realtimeVoiceLanguageSchema = z
  .string()
  .trim()
  .optional()
  .superRefine((val, ctx) => {
    if (!val) return
    if (!LANGUAGE_VALUES.includes(val as (typeof LANGUAGE_VALUES)[number])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: {
          i18n: {
            key: 'custom.invalid_enum_value',
            values: {
              options: LANGUAGE_VALUES.join(', '),
              received: val,
            },
          },
        },
      })
    }
  })
export const ChatbotUpdateRealtimeVoiceChatSchema = z.object({
  realtimeVoiceLanguage: realtimeVoiceLanguageSchema,
})

// calendar.google.com/calendar[/u/<n>]/appointments/schedules/<id>
const BOOKING_GOOGLE_SCHEDULE_PATH_RE = /^\/calendar(?:\/u\/\d+)?\/appointments\/schedules\/[A-Za-z0-9_-]+\/?$/
const BOOKING_MICROSOFT_HOSTS = ['outlook.office365.com', 'outlook.office.com', 'bookings.cloud.microsoft']
const BOOKING_MICROSOFT_PAGE_PATH_RES = [/^\/book\/[^/]+\/?$/, /^\/owa\/calendar\/[^/]+\/bookings\/?$/]

const parseHttpsLink = (value: string): URL | null => {
  // Whitespace or control characters mean it is not a link as copied
  if (/[\s\p{Cc}]/u.test(value)) return null
  try {
    const url = new URL(value)
    // Credentials let a link show one host and open another
    return url.protocol === 'https:' && !url.username && !url.password ? url : null
  } catch {
    return null
  }
}

// Any https link is valid; unframeable ones open in a new tab
export const isHttpsLink = (value: string): boolean => parseHttpsLink(value) !== null

const bannerPresentationShape = {
  style: z.enum(CHAT_BANNER_STYLES),
  ratio: z.enum(CHAT_BANNER_RATIOS),
  position: z.enum(CHAT_BANNER_POSITIONS),
  buttonSize: z.enum(CHAT_BANNER_BUTTON_SIZES),
  action: z.enum(CHAT_BANNER_ACTIONS),
}

// Editable settings keep a ratio for every style. Required content is checked only
// while enabled so an unfinished banner can still be turned off.
const chatBannerSettingsFields = z.object({
  enabled: z.boolean().default(false),
  style: bannerPresentationShape.style.default('compact'),
  ratios: z
    .object({
      compact: z.enum(CHAT_BANNER_RATIOS_BY_STYLE.compact).default('16:9'),
      image: z.enum(CHAT_BANNER_RATIOS_BY_STYLE.image).default('6:1'),
      full: z.enum(CHAT_BANNER_RATIOS_BY_STYLE.full).default('4:1'),
    })
    .default({}),
  title: z.string().max(40).default(''),
  description: z.string().max(60).default(''),
  position: bannerPresentationShape.position.default('top'),
  ctaEnabled: z.boolean().default(true),
  buttonText: z.string().max(20).optional(),
  buttonSize: bannerPresentationShape.buttonSize.default('medium'),
  action: bannerPresentationShape.action.default('link'),
  linkUrl: z.string().default(''),
  closable: z.boolean().default(true),
  altText: z.string().max(100).default(''),
  imageUrl: z
    .string()
    .refine((value) => value === '' || (value.startsWith('https://') && isHttpsLink(value)))
    .default(''),
})

export const ChatBannerSettingsSchema = chatBannerSettingsFields
  .transform((settings) => ({
    ...settings,
    buttonText: settings.buttonText ?? (settings.action === 'booking' ? 'Book now' : 'Learn more'),
  }))
  .superRefine((settings, ctx) => {
    if (!settings.enabled) return
    if (!settings.imageUrl) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['imageUrl'] })
    }
    if (settings.ctaEnabled && !settings.buttonText.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['buttonText'] })
    }
    if (settings.action === 'link' && (!settings.linkUrl.startsWith('https://') || !isHttpsLink(settings.linkUrl))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['linkUrl'] })
    }
  })

export type ChatBannerSettingsSchemaType = z.infer<typeof ChatBannerSettingsSchema>

/** Recover persisted settings independently, including each style's saved ratio. */
export function parseChatBannerSettings(input: unknown): ChatBannerSettingsSchemaType {
  const source = z.record(z.unknown()).safeParse(input)
  const fields = source.success ? source.data : {}
  const recovered = Object.fromEntries(
    Object.entries(chatBannerSettingsFields.shape).map(([name, schema]) => {
      if (name === 'ratios') {
        const ratios = z.record(z.unknown()).safeParse(fields.ratios)
        return [
          name,
          Object.fromEntries(
            Object.entries(chatBannerSettingsFields.shape.ratios.removeDefault().shape).map(([style, ratioSchema]) => {
              const parsed = ratioSchema.safeParse(ratios.success ? ratios.data[style] : undefined)
              return [style, parsed.success ? parsed.data : ratioSchema.parse(undefined)]
            }),
          ),
        ]
      }
      const parsed = schema.safeParse(fields[name])
      return [name, parsed.success ? parsed.data : schema.parse(undefined)]
    }),
  )
  // Required-content checks belong to the editable form, not saved-field recovery.
  const parsed = ChatBannerSettingsSchema.parse({ ...recovered, enabled: false })
  return { ...parsed, enabled: recovered.enabled === true }
}

// BE gates the CDN origin; invalid public config hides only the banner.
export const PublicChatBannerSchema = z
  .object({
    ...bannerPresentationShape,
    imageUrl: z.string().refine(isHttpsLink),
    title: z.string().max(40).optional(),
    description: z.string().max(60).optional(),
    altText: z.string().max(100).optional(),
    ctaEnabled: z.boolean(),
    buttonText: z.string().max(20).optional(),
    closable: z.boolean(),
    linkUrl: z.string().refine(isHttpsLink).optional(),
    bookingLinkOnly: z.boolean().optional(),
    bookingUrl: z.string().refine(isHttpsLink).optional(),
  })
  .refine(({ style, ratio }) => CHAT_BANNER_RATIOS_BY_STYLE[style].some((allowed) => allowed === ratio))
  .refine(({ action, linkUrl, bookingLinkOnly, bookingUrl }) =>
    action === 'link'
      ? !!linkUrl && bookingLinkOnly === undefined && bookingUrl === undefined
      : linkUrl === undefined && (bookingLinkOnly === true ? !!bookingUrl : bookingUrl === undefined),
  )
  .nullable()
  .catch(null)

// Events use the effective style, so a collapsed banner can keep its wide ratio.
export const RecordBannerEventSchema = z
  .object({
    ...bannerPresentationShape,
    chatbotId: z.string().regex(/^[a-f\d]{24}$/i),
    sessionId: z.string().trim().min(1).max(100),
    event: z.enum(CHAT_BANNER_EVENTS),
    hasCta: z.boolean(),
    clickedPart: z.enum(CHAT_BANNER_CLICKED_PARTS).optional(),
  })
  .refine(({ event, clickedPart }) => event !== 'banner_clicked' || clickedPart !== undefined)
export type RecordBannerEventInput = z.infer<typeof RecordBannerEventSchema>

// Mirrors the pages the backend frames. Only these are framed, whatever provider the payload names.
export const isEmbeddableBookingUrl = (value: string): boolean => {
  const url = parseHttpsLink(value)
  if (!url) return false
  const host = url.hostname.toLowerCase()
  if (host === 'calendar.google.com') return BOOKING_GOOGLE_SCHEDULE_PATH_RE.test(url.pathname)
  if (BOOKING_MICROSOFT_HOSTS.includes(host)) {
    return BOOKING_MICROSOFT_PAGE_PATH_RES.some((pathRe) => pathRe.test(url.pathname))
  }
  return (host === 'bookings.office.com' || host.endsWith('.bookings.office.com')) && url.pathname !== '/'
}

export const ChatbotUpdateBookingSchedulingUrlSchema = z.object({
  // Empty turns booking off
  bookingSchedulingUrl: z
    .string()
    .trim()
    .refine((value) => value === '' || isHttpsLink(value), {
      params: { i18n: { key: 'custom.booking.invalid_scheduling_url' } },
    }),
  // Empty turns the trigger off; a typed leading # is dropped
  bookingTriggerHash: z
    .string()
    .trim()
    .transform((value) => value.replace(/^#/, ''))
    .refine((value) => /^[A-Za-z0-9_-]{0,64}$/.test(value), {
      params: { i18n: { key: 'custom.booking.invalid_trigger_hash' } },
    }),
})
export type ChatbotUpdateBookingSchedulingUrlSchemaType = z.infer<typeof ChatbotUpdateBookingSchedulingUrlSchema>
export type ChatbotUpdateRealtimeVoiceChatSchemaType = z.infer<typeof ChatbotUpdateRealtimeVoiceChatSchema>

export const PublicChatbotUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(30),
    welcomeMessage: z.string().trim().min(1).max(500),
    themeColor: z.string().trim(),
    logo: z.any() as ZodType<File>,
  })
  .refine(
    ({ logo }) => {
      if (!logo?.size) return true
      // check if file format is supported
      const imageFormat = `.${logo?.name.split('.').pop()}`.toLowerCase()

      return (
        imageFormat &&
        CHATBOT_LOGO_SUPPORTED_FORMATS.includes(imageFormat) &&
        CHATBOT_LOGO_SUPPORTED_IMAGE_TYPES.includes(logo?.type)
      )
    },
    {
      params: {
        i18n: {
          key: 'custom.file.not_supported',
          values: { formats: CHATBOT_LOGO_SUPPORTED_FORMATS.join(', ') },
        },
      },
      path: ['logo'],
    },
  )
  .refine(
    ({ logo }) => {
      // check if file size is less than CHATBOT_LOGO_DATA_SIZE_LIMIT
      const imageSize = logo?.size || 0
      const imageSizeInMB = imageSize / (1024 * 1024)

      return imageSizeInMB <= CHATBOT_LOGO_DATA_SIZE_LIMIT
    },
    {
      params: {
        i18n: {
          key: 'custom.file.too_large',
          values: { maxSize: `${CHATBOT_LOGO_DATA_SIZE_LIMIT}MB` },
        },
      },
      path: ['logo'],
    },
  )
  .refine(
    ({ themeColor }) => {
      return isHexColor(themeColor)
    },
    {
      params: {
        i18n: {
          key: 'custom.others.invalid',
        },
      },
      path: ['themeColor'],
    },
  )
export type ChatbotUpdateSchemaType = z.infer<typeof ChatbotUpdateSchema>

export type PublicChatbotUpdateSchemaType = z.infer<typeof PublicChatbotUpdateSchema>

export const ChatbotCollectContactSchema = z.object({
  name: z.string().trim().min(1).max(30),
  email: z.string().trim().min(1).email().max(128),
})
export type ChatbotCollectContactSchemaType = z.infer<typeof ChatbotCollectContactSchema>

export const ChatbotEditPasswordSchema = z
  .object({
    isPasswordProtected: z.boolean(),
    password: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (!data.isPasswordProtected) return

    if (data.password.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: 'string',
        inclusive: true,
        minimum: 1,
        path: ['password'],
      })
    }

    return
  })

export type ChatbotEditPasswordSchemaType = z.infer<typeof ChatbotEditPasswordSchema>

export const ChatbotLoginSchema = z
  .object({
    password: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (data.password.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: 'string',
        inclusive: true,
        minimum: 1,
        path: ['password'],
      })
    }

    return
  })
export type ChatbotLoginSchemaType = z.infer<typeof ChatbotLoginSchema>
